import { api } from '../lib/api'

export interface MailingListEntry {
  id: string
  email: string
  firstName?: string
  lastName?: string
  signupType: 'beta' | 'newsletter' | 'updates'
  source?: string
  status: 'active' | 'unsubscribed' | 'bounced'
  privacyConsent: boolean
  marketingConsent: boolean
  metadata?: any
  unsubscribedAt?: string
  inviteStatus: 'not_sent' | 'sent' | 'accepted' | 'declined'
  inviteSentAt?: string
  inviteId?: string
  inviteAcceptedAt?: string
  adminNotes?: string
  lastEmailedAt?: string
  createdAt: string
  updatedAt: string
}

export interface MailingListStats {
  total: number
  active: number
  beta: number
  newsletter: number
  unsubscribed: number
}

export const mailingListService = {
  // Get all mailing list entries with optional filtering
  async getEntries(params?: {
    signupType?: 'beta' | 'newsletter' | 'updates'
    status?: 'active' | 'unsubscribed' | 'bounced'
    limit?: number
    offset?: number
  }): Promise<{ entries: MailingListEntry[]; total: number }> {
    const response = await api.get('/mailing-list/admin/entries', { params })
    return response.data
  },

  // Get mailing list statistics
  async getStats(): Promise<MailingListStats> {
    const response = await api.get('/mailing-list/stats')
    return response.data.data
  },

  // Update a mailing list entry
  async updateEntry(id: string, data: Partial<MailingListEntry>): Promise<MailingListEntry> {
    const response = await api.patch(`/mailing-list/admin/entries/${id}`, data)
    return response.data
  },

  // Delete a mailing list entry
  async deleteEntry(id: string): Promise<void> {
    await api.delete(`/mailing-list/admin/entries/${id}`)
  },

  // Export mailing list entries
  async exportEntries(format: 'csv' | 'json' = 'csv', params?: {
    signupType?: 'beta' | 'newsletter' | 'updates'
    status?: 'active' | 'unsubscribed' | 'bounced'
  }): Promise<Blob> {
    const response = await api.get('/mailing-list/admin/export', {
      params: { ...params, format },
      responseType: 'blob'
    })
    return response.data
  },

  // Send test email to a subscriber
  async sendTestEmail(id: string, subject: string, content: string): Promise<void> {
    await api.post(`/mailing-list/admin/entries/${id}/send-test`, {
      subject,
      content
    })
  },

  // Bulk actions
  async bulkUpdateStatus(
    ids: string[],
    status: 'active' | 'unsubscribed' | 'bounced'
  ): Promise<void> {
    await api.post('/mailing-list/admin/bulk-update', {
      ids,
      status
    })
  },

  // Invite management
  async linkInvite(id: string, inviteId: string): Promise<MailingListEntry> {
    const response = await api.patch(`/mailing-list/admin/entries/${id}/link-invite`, { inviteId })
    return response.data.data
  },

  async updateAdminNotes(id: string, notes: string): Promise<MailingListEntry> {
    const response = await api.patch(`/mailing-list/admin/entries/${id}/notes`, { notes })
    return response.data.data
  },

  async updateInviteStatus(
    id: string,
    inviteStatus: 'not_sent' | 'sent' | 'accepted' | 'declined'
  ): Promise<MailingListEntry> {
    const response = await api.patch(`/mailing-list/admin/entries/${id}/invite-status`, { inviteStatus })
    return response.data.data
  },

  async bulkUpdateInviteStatus(
    ids: string[],
    inviteStatus: 'not_sent' | 'sent' | 'accepted' | 'declined'
  ): Promise<void> {
    await api.post('/mailing-list/admin/bulk-update-invite-status', {
      ids,
      inviteStatus
    })
  },

  // Campaign invite creation and retrieval
  async createCampaignInvite(data: {
    code: string
    maxUses?: number
    expiresAt?: string
    incentiveOfferId?: string
  }): Promise<any> {
    const response = await api.post('/referral-system/admin/invites', data)
    return response.data
  },

  async getInviteDetails(inviteId: string): Promise<any> {
    const response = await api.get(`/referral-system/admin/invites/${inviteId}`)
    return response.data
  }
}